import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '../../../common/button-new';
import { useSnackbar } from '../../../../hooks';
import { VideoButton } from '../video-button/video-button';
import { Page } from '../page/page';
import { ReactComponent as AcuvueLogo } from '../../../../assets/images/video/acuvue-logo.svg';
import { Veeva } from '../../../common/veeva';
import { v2026PP14563 } from '../../../../constants/veeva';

import { PromoVideoLandingPageProps } from './eyes-promo-video-page.models';
import { useStyles } from './eyes-promo-video-page.styles';

export const PromoVideoLandingPage = ({ page }: PromoVideoLandingPageProps): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const handleShareClick = useCallback(async (): Promise<void> => {
    const url = window.location.href;

    try {
      if (navigator.share) {
        await navigator.share({ url });
        return;
      }

      if (!navigator.clipboard) {
        throw new Error('Clipboard API is unavailable');
      }

      await navigator.clipboard.writeText(url);
      enqueueSnackbar(<FormattedMessage id="promoVideo.share.success" />, { variant: 'success' });
    } catch {
      enqueueSnackbar(<FormattedMessage id="promoVideo.share.error" />, { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  return (
    <Page title={intl.formatMessage({ id: `promoVideo.metaTitle.${page.slug}` })}>
      <main className={classes.main} style={{ background: page.background }}>
          <h1 className={classes.title}>
            <AcuvueLogo className={classes.logo} aria-hidden="true" />
            <FormattedMessage id="promoVideo.title" />
          </h1>
          <VideoButton
            mobileVideoSrc={page.mobileVideoSrc}
            desktopVideoSrc={page.desktopVideoSrc}
            label={page.slug}
            imgSrc={page.previewSrc}
            imgClasses={classes.preview}
            videoId={`promo-video-${page.slug}`}
            rootClasses={classes.previewButton}
            hideText
            ariaLabel="promoVideo.preview.label"
            testId={`promo-video-preview-${page.slug}`}
          />
          <Button
            style="primary"
            rootClasses={classes.share}
            onClick={handleShareClick}
            ariaLabel="promoVideo.share.label"
            testId={`promo-video-share-${page.slug}`}
          >
            <FormattedMessage id="promoVideo.share.label" />
          </Button>
          <div className={classes.veeva}>
            <Veeva veevaNumber={v2026PP14563} isRight />
          </div>
      </main>
    </Page>
  );
};